import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import type { User, AuthContextType, LoginResponse } from "../types/Auth";
import {
  login as authLogin,
  logout as authLogout,
  validateToken as authValidateToken,
} from "../services/auth";
import {
  getToken as storageGetToken,
  setToken as storageSetToken,
} from "../lib/tokenStorage";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize from storage (SSR-safe helper handles non-browser)
  const [token, setToken] = useState<string | null>(() => storageGetToken());
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: validate token -> fetch user
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      setLoading(true);
      try {
        const currentToken = storageGetToken();
        if (currentToken) {
          const result = await authValidateToken();
          if (!cancelled) {
            if (result.success && result.user) {
              setUser(result.user);
              setToken(currentToken);
            } else {
              // invalid token -> clear
              setUser(null);
              setToken(null);
              storageSetToken(null);
            }
          }
        } else {
          if (!cancelled) {
            setUser(null);
            setToken(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Token validation failed:", err);
          setUser(null);
          setToken(null);
          storageSetToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep tabs/windows in sync (logout/login elsewhere)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_token") {
        const newToken = e.newValue;
        setToken(newToken);
        if (!newToken) {
          setUser(null);
        } else {
          // Optionally revalidate here if you want instant user refresh:
          // authValidateToken().then(r => r.success && r.user ? setUser(r.user) : setUser(null));
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<LoginResponse> => {
    try {
      const result = await authLogin({ username, password });
      if (result.success && result.user && result.token) {
        setUser(result.user);
        setToken(result.token);
      }
      return result;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch (error) {
      console.error("Logout error:", error);
      // even on error, proceed with local cleanup
    } finally {
      setUser(null);
      setToken(null);
      storageSetToken(null);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user, // or `!!token` depending on your preference
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
